import { useState, useMemo } from 'react';
import { RefreshCw, Plus, FileCode, Settings, TestTube, Tag } from 'lucide-react';
import { cn } from '@/lib/utils';
import StatsCard from '@/components/common/StatsCard';
import WorkflowTemplatesTable from '@/components/workflows/WorkflowTemplatesTable';
import WorkflowTypesTable from '@/components/workflows/WorkflowTypesTable';
import WorkflowTypeDefaultsTable from '@/components/workflows/WorkflowTypeDefaultsTable';
import CreateTemplateModal from '@/components/workflows/CreateTemplateModal';
import TemplateDetailModal from '@/components/workflows/TemplateDetailModal';
import ResolutionTester from '@/components/workflows/ResolutionTester';
import { useWorkflowTemplates, useWorkflowTypes, useWorkflowTypeDefaults } from '@/hooks/useWorkflows';
import type { WorkflowTemplate, WorkflowEnvironment } from '@/lib/types';

type TabType = 'templates' | 'types' | 'defaults';

// Shared stable empty fallback so an absent query result doesn't hand `stats` a fresh [] each render.
const EMPTY: never[] = [];

export default function WorkflowsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [showResolutionTester, setShowResolutionTester] = useState(false);
  const [filterEnvironment, setFilterEnvironment] = useState<WorkflowEnvironment | ''>('');

  const {
    data: templatesData,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = useWorkflowTemplates(
    filterEnvironment ? { environment: filterEnvironment, limit: 500 } : { limit: 500 }
  );

  const { data: typesData, refetch: refetchTypes } = useWorkflowTypes();
  const { data: defaultsData, refetch: refetchDefaults } = useWorkflowTypeDefaults();

  const templates = templatesData?.items ?? EMPTY;
  const types = typesData?.items ?? EMPTY;
  const defaults = defaultsData?.items ?? EMPTY;

  const stats = useMemo(() => {
    const active = templates.filter((t) => !t.disable_ts);

    return {
      totalTemplates: templates.length,
      activeTemplates: active.length,
      types: types.length,
      defaults: defaults.length,
      production: templates.filter((t) => t.environment === 'production' && !t.disable_ts).length,
      staging: templates.filter((t) => t.environment === 'staging' && !t.disable_ts).length,
      development: templates.filter((t) => t.environment === 'development' && !t.disable_ts).length,
    };
  }, [templates, types, defaults]);

  const handleRefresh = () => {
    if (activeTab === 'templates') {
      void refetchTemplates();
    } else if (activeTab === 'types') {
      void refetchTypes();
    } else {
      void refetchDefaults();
    }
  };

  const tabs: { id: TabType; label: string; count: number }[] = [
    { id: 'templates', label: 'Templates', count: stats.totalTemplates },
    { id: 'types', label: 'Types', count: stats.types },
    { id: 'defaults', label: 'Defaults', count: stats.defaults },
  ];

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workflow Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage workflow templates, types, and default configurations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowResolutionTester(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-secondary/50 hover:bg-secondary text-foreground border border-border transition-colors"
          >
            <TestTube className="h-4 w-4" />
            <span className="text-sm font-medium">Test Resolution</span>
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="text-sm font-medium">Refresh</span>
          </button>
          {activeTab === 'templates' && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">New Template</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatsCard
          label="Active / Total"
          value={`${stats.activeTemplates}/${stats.totalTemplates}`}
          icon={FileCode}
          variant="default"
        />
        <StatsCard
          label="Types"
          value={stats.types.toString()}
          icon={Tag}
          variant="primary"
        />
        <StatsCard
          label="Production"
          value={stats.production.toString()}
          icon={Settings}
          variant="success"
        />
        <StatsCard
          label="Staging"
          value={stats.staging.toString()}
          icon={Settings}
          variant="warning"
        />
        <StatsCard
          label="Defaults"
          value={stats.defaults.toString()}
          icon={Settings}
          variant="default"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1" role="tablist" aria-label="Workflow management tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
              <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="glass-card rounded-lg overflow-hidden p-6">
        {activeTab === 'templates' && (
          <div
            id="panel-templates"
            role="tabpanel"
            aria-labelledby="tab-templates"
            className="space-y-4"
          >
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label htmlFor="env-filter" className="text-sm text-muted-foreground">
                  Environment:
                </label>
                <select
                  id="env-filter"
                  value={filterEnvironment}
                  onChange={(e) => setFilterEnvironment(e.target.value as WorkflowEnvironment | '')}
                  className="px-3 py-1.5 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">All</option>
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="development">Development</option>
                </select>
              </div>
            </div>

            <WorkflowTemplatesTable
              templates={templates}
              loading={templatesLoading}
              onSelectTemplate={setSelectedTemplate}
            />
          </div>
        )}

        {activeTab === 'types' && (
          <div id="panel-types" role="tabpanel" aria-labelledby="tab-types">
            <WorkflowTypesTable />
          </div>
        )}

        {activeTab === 'defaults' && (
          <div id="panel-defaults" role="tabpanel" aria-labelledby="tab-defaults">
            <WorkflowTypeDefaultsTable />
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTemplateModal types={types} onClose={() => setShowCreateModal(false)} />
      )}

      {selectedTemplate && (
        <TemplateDetailModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}

      {showResolutionTester && (
        <ResolutionTester types={types} onClose={() => setShowResolutionTester(false)} />
      )}
    </div>
  );
}
